'use client'

import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react'
import { useRouter } from 'next/navigation'

// Event types from server
export type SSEEventType =
    | 'connected'
    | 'ping'
    | 'update'
    | 'project_update'
    | 'container_update'
    | 'deploy_log'
    | 'container_log'
    | 'vds_stage_build'
    | 'vds_deploy_pending'
    | 'vds_deploy_approved'
    | 'vds_deploy_rollback'
    | 'vds_deploy_failed'
    // Chat/Agent events
    | 'chat_update'
    | 'chat_message'
    | 'run_status'
    | 'run_usage'

export interface SSEEvent {
    type: SSEEventType
    timestamp?: number
    // Project events
    project?: string
    status?: string
    log?: string
    // Container events
    action?: string
    containerId?: string
    containerName?: string
    // Chat/Agent events
    chatId?: string
    runId?: string
    chat?: unknown
    message?: unknown
    usage?: unknown
}

type EventCallback = (event: SSEEvent) => void
type UnsubscribeFn = () => void

interface SSEContextValue {
    isConnected: boolean
    subscribe: (eventType: SSEEventType, callback: EventCallback) => UnsubscribeFn
}

const SSEContext = createContext<SSEContextValue>({
    isConnected: false,
    subscribe: () => () => {},
})

export function useSSE() {
    return useContext(SSEContext)
}

// Hook for subscribing to specific SSE events
export function useSSEEvent(
    eventType: SSEEventType | SSEEventType[],
    callback: EventCallback,
    deps: React.DependencyList = []
) {
    const { subscribe } = useSSE()
    const callbackRef = useRef(callback)
    
    // Keep callback ref up to date
    useEffect(() => {
        callbackRef.current = callback
    })
    
    useEffect(() => {
        const types = Array.isArray(eventType) ? eventType : [eventType]
        const unsubscribes = types.map(type =>
            subscribe(type, (event) => callbackRef.current(event))
        )
        return () => unsubscribes.forEach(unsub => unsub())
    // Bug #12: Include eventType in dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribe, eventType, ...deps])
}

// Hook for project-specific log updates
export function useProjectLogs(projectName: string | null) {
    const [log, setLog] = useState('')
    const [status, setStatus] = useState<string>('')
    
    useSSEEvent('deploy_log', (event) => {
        if (event.project === projectName && event.log !== undefined) {
            setLog(event.log)
        }
        if (event.project === projectName && event.status) {
            setStatus(event.status)
        }
    }, [projectName])
    
    useSSEEvent('project_update', (event) => {
        if (event.project === projectName && event.status) {
            setStatus(event.status)
        }
    }, [projectName])
    
    const reset = useCallback(() => {
        setLog('')
        setStatus('')
    }, [])
    
    return { log, status, reset }
}

// Hook for container log updates
export function useContainerLogs(containerId: string | null) {
    const [log, setLog] = useState('')
    
    useSSEEvent('container_log', (event) => {
        if (event.containerId === containerId && event.log !== undefined) {
            setLog(event.log)
        }
    }, [containerId])
    
    const reset = useCallback(() => {
        setLog('')
    }, [])
    
    return { log, reset }
}

interface SSEProviderProps {
    children: ReactNode
}

export function SSEProvider({ children }: SSEProviderProps) {
    const router = useRouter()
    const routerRef = useRef(router)
    const eventSourceRef = useRef<EventSource | null>(null)
    const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const wasConnectedRef = useRef(false)
    const reconnectAttemptsRef = useRef(0)
    const [isConnected, setIsConnected] = useState(false)
    
    // Subscription management
    const subscribersRef = useRef<Map<SSEEventType, Set<EventCallback>>>(new Map())
    
    // Keep router ref up to date
    routerRef.current = router
    
    const subscribe = useCallback((eventType: SSEEventType, callback: EventCallback): UnsubscribeFn => {
        if (!subscribersRef.current.has(eventType)) {
            subscribersRef.current.set(eventType, new Set())
        }
        subscribersRef.current.get(eventType)!.add(callback)
        
        return () => {
            subscribersRef.current.get(eventType)?.delete(callback)
        }
    }, [])
    
    const notifySubscribers = useCallback((event: SSEEvent) => {
        const subscribers = subscribersRef.current.get(event.type)
        if (subscribers) {
            subscribers.forEach(callback => {
                try {
                    callback(event)
                } catch (e) {
                    console.error('SSE subscriber error:', e)
                }
            })
        }
    }, [])

    useEffect(() => {
        const connect = () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close()
            }

            // Connect to Next.js SSE proxy endpoint (handles auth via session cookie)
            const sseUrl = '/api/events'
            console.log('[SSE Provider] Connecting to SSE proxy')
            const eventSource = new EventSource(sseUrl)
            eventSourceRef.current = eventSource

            eventSource.onmessage = (event) => {
                try {
                    const data: SSEEvent = JSON.parse(event.data)
                    
                    if (data.type === 'connected') {
                        setIsConnected(true)
                        reconnectAttemptsRef.current = 0 // Reset backoff on successful connection
                        if (wasConnectedRef.current) {
                            console.log('[SSE] reconnected')
                            routerRef.current.refresh()
                        } else {
                            console.log('[SSE] connected')
                        }
                        wasConnectedRef.current = true
                        return
                    }
                    
                    if (data.type === 'ping') return
                    
                    // Log non-ping events
                    console.log('[SSE]', JSON.stringify(data))
                    
                    // Notify subscribers first
                    notifySubscribers(data)
                    
                    // For log events, don't refresh - subscribers handle the data directly
                    if (data.type === 'deploy_log' || data.type === 'container_log') {
                        return
                    }
                    
                    // State change events are handled by individual components via useSSEEvent
                    // - container_update → ContainerList refetches
                    // - project_update → ProjectList refetches
                    // router.refresh() only happens on SSE reconnect (rare case)
                } catch (e) {
                    console.error('Failed to parse SSE:', e)
                }
            }

            eventSource.onerror = () => {
                setIsConnected(false)
                eventSource.close()
                // Exponential backoff: 5s, 10s, 20s, 40s, max 60s
                reconnectAttemptsRef.current += 1
                const backoff = Math.min(5000 * Math.pow(2, reconnectAttemptsRef.current - 1), 60000)
                console.log(`[SSE] Connection error, reconnecting in ${backoff/1000}s (attempt ${reconnectAttemptsRef.current})`)
                reconnectTimeoutRef.current = setTimeout(connect, backoff)
            }
        }

        connect()

        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close()
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current)
            }
        }
    }, [notifySubscribers])

    return (
        <SSEContext.Provider value={{ isConnected, subscribe }}>
            {children}
        </SSEContext.Provider>
    )
}
